import { useEffect, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ColorThemeRow = {
  id: string;
  theme_key: string;
  label: string;
  group_name: string;
  bg_color: string;
  text_color: string;
  border_color: string | null;
  usage_type:
    | "core"
    | "button"
    | "card"
    | "pill"
    | "badge"
    | "sidebar"
    | "calendar"
    | "custom";
  is_system: boolean;
  is_active: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type ThemeStyle = {
  bg_color: string;
  text_color: string;
  border_color: string | null;
};

type ThemeDefault = ThemeStyle & {
  label: string;
  group_name: string;
  usage_type: ColorThemeRow["usage_type"];
};

const CORE_DEFAULTS: Record<string, ThemeDefault> = {
  sidebar_active: { label: "Sidebar Active Item", group_name: "Core UI Colors", usage_type: "sidebar", bg_color: "#18181B", text_color: "#FFFFFF", border_color: null },
  card_lead: { label: "Lead KPI Card", group_name: "Core UI Colors", usage_type: "card", bg_color: "#18181B", text_color: "#FFFFFF", border_color: null },
  button_primary: { label: "Primary Button", group_name: "Buttons", usage_type: "button", bg_color: "#18181B", text_color: "#FFFFFF", border_color: null },
  button_action: { label: "Action / Submit Button", group_name: "Buttons", usage_type: "button", bg_color: "#7C3AED", text_color: "#FFFFFF", border_color: null },
};

export const STATUS_THEME_KEYS = [
  "status_done",
  "status_pending",
  "status_review",
  "status_in_progress",
  "status_correction",
  "status_none",
] as const;

const STATUS_DEFAULTS: Record<(typeof STATUS_THEME_KEYS)[number], ThemeDefault> = {
  status_done: { label: "Done / Completed / Approved", group_name: "Status Pills", usage_type: "pill", bg_color: "#DCFCE7", text_color: "#16A34A", border_color: null },
  status_pending: { label: "Pending", group_name: "Status Pills", usage_type: "pill", bg_color: "#FEF3C7", text_color: "#B45309", border_color: null },
  status_review: { label: "Review / Waiting", group_name: "Status Pills", usage_type: "pill", bg_color: "#DBEAFE", text_color: "#1D4ED8", border_color: null },
  status_in_progress: { label: "Work in Progress", group_name: "Status Pills", usage_type: "pill", bg_color: "#F3E8FF", text_color: "#7E22CE", border_color: null },
  status_correction: { label: "Correction / Rejected / Overdue", group_name: "Status Pills", usage_type: "pill", bg_color: "#FEE2E2", text_color: "#B91C1C", border_color: null },
  status_none: { label: "None", group_name: "Status Pills", usage_type: "pill", bg_color: "#F3F4F6", text_color: "#6B7280", border_color: null },
};

const FIELD_OPTION_PALETTE: ThemeStyle[] = [
  { bg_color: "#DCFCE7", text_color: "#166534", border_color: null },
  { bg_color: "#FEF3C7", text_color: "#92400E", border_color: null },
  { bg_color: "#DBEAFE", text_color: "#1E40AF", border_color: null },
  { bg_color: "#F3E8FF", text_color: "#6B21A8", border_color: null },
  { bg_color: "#FCE7F3", text_color: "#9D174D", border_color: null },
  { bg_color: "#CCFBF1", text_color: "#0F766E", border_color: null },
  { bg_color: "#EDE9FE", text_color: "#5B21B6", border_color: null },
  { bg_color: "#E0E7FF", text_color: "#3730A3", border_color: null },
  { bg_color: "#ECFCCB", text_color: "#3F6212", border_color: null },
  { bg_color: "#FEE2E2", text_color: "#991B1B", border_color: null },
  { bg_color: "#CFFAFE", text_color: "#155E75", border_color: null },
  { bg_color: "#FAE8FF", text_color: "#86198F", border_color: null },
  { bg_color: "#F1F5F9", text_color: "#334155", border_color: null },
  { bg_color: "#D1FAE5", text_color: "#047857", border_color: null },
  { bg_color: "#FEF9C3", text_color: "#854D0E", border_color: null },
];

function createFieldSlotDefaults(): Record<string, ThemeDefault> {
  const rows: Record<string, ThemeDefault> = {};
  for (let fieldSlot = 1; fieldSlot <= 4; fieldSlot++) {
    for (let option = 1; option <= 15; option++) {
      const base = FIELD_OPTION_PALETTE[(option - 1) % FIELD_OPTION_PALETTE.length];
      rows[`field_slot_${fieldSlot}_option_${option}`] = {
        label: `Option Color ${option}`,
        group_name: `Field Slot ${fieldSlot} Colors`,
        usage_type: "pill",
        bg_color: base.bg_color,
        text_color: base.text_color,
        border_color: base.border_color,
      };
    }
  }
  return rows;
}

export const FIELD_SLOT_KEYS = [1, 2, 3, 4] as const;
export const FIELD_OPTION_COUNT = 15;

export const DEFAULT_THEME: Record<string, ThemeDefault> = {
  ...CORE_DEFAULTS,
  ...STATUS_DEFAULTS,
  ...createFieldSlotDefaults(),
};

let cache: Record<string, ThemeStyle> = {};
const listeners = new Set<() => void>();

for (const [k, v] of Object.entries(DEFAULT_THEME)) {
  cache[k] = { bg_color: v.bg_color, text_color: v.text_color, border_color: v.border_color };
}

function setCache(rows: ColorThemeRow[]) {
  const next: Record<string, ThemeStyle> = {};
  for (const [k, v] of Object.entries(DEFAULT_THEME)) {
    next[k] = { bg_color: v.bg_color, text_color: v.text_color, border_color: v.border_color };
  }
  for (const r of rows) {
    if (!r.is_active) continue;
    next[r.theme_key] = { bg_color: r.bg_color, text_color: r.text_color, border_color: r.border_color };
  }
  cache = next;
  listeners.forEach((l) => l());
}

export function getThemeStyle(themeKey: string): ThemeStyle | null {
  return cache[themeKey] ?? null;
}

export function normalizeThemeKey(value: string): string {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[—–-]/g, "_")
    .replace(/\//g, "_")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

const STATUS_ALIASES: Record<string, string> = {
  done: "status_done",
  completed: "status_done",
  complete: "status_done",
  approved: "status_done",
  active: "status_done",
  paid: "status_done",
  sent: "status_done",
  hired: "status_done",
  pending: "status_pending",
  on_notice: "status_pending",
  on_hold: "status_pending",
  waiting: "status_review",
  waiting_for_approval: "status_review",
  review: "status_review",
  changes_requested: "status_review",
  changes: "status_review",
  scheduled: "status_review",
  in_progress: "status_in_progress",
  work_in_progress: "status_in_progress",
  correction: "status_correction",
  correction_needed: "status_correction",
  needs_correction: "status_correction",
  rejected: "status_correction",
  overdue: "status_correction",
  failed: "status_correction",
  terminated: "status_correction",
  suspended: "status_correction",
  all: "status_none",
  none: "status_none",
  inactive: "status_none",
  returned: "status_none",
};

export function getStatusThemeKey(value: string): string | null {
  return STATUS_ALIASES[normalizeThemeKey(value)] ?? null;
}

export function isStatusValue(value: string): boolean {
  return Boolean(getStatusThemeKey(value));
}

export function getFieldSlotOptionKey(fieldSlotIndex: number, optionIndex: number): string {
  const field = Math.min(4, Math.max(1, Math.floor(fieldSlotIndex) + 1));
  const option = Math.min(FIELD_OPTION_COUNT, Math.max(1, Math.floor(optionIndex) + 1));
  return `field_slot_${field}_option_${option}`;
}

export function getFieldSlotOptionStyle(fieldSlotIndex: number, optionIndex: number): ThemeStyle {
  const key = getFieldSlotOptionKey(fieldSlotIndex, optionIndex);
  return cache[key] ?? DEFAULT_THEME[key] ?? cache.field_slot_1_option_1;
}

function hashOptionIndex(value: string): number {
  const s = normalizeThemeKey(value);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h) % FIELD_OPTION_COUNT;
}

/**
 * Resolve style for selected pills/badges.
 * - Status values always use fixed semantic colours.
 * - Non-status values use generic field position + option position.
 */
export function getPillStyle(value: string, fieldSlotIndex = 0, optionIndex?: number): ThemeStyle {
  const statusKey = getStatusThemeKey(value);
  if (statusKey && cache[statusKey]) return cache[statusKey];
  return getFieldSlotOptionStyle(fieldSlotIndex, typeof optionIndex === "number" ? optionIndex : hashOptionIndex(value));
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot() {
  return cache;
}

export function useThemeCache() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useColorThemeFetcher() {
  return useQuery({
    queryKey: ["ui_color_theme"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ui_color_theme" as never)
        .select("*")
        .order("group_name", { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as unknown as ColorThemeRow[];
      setCache(rows);
      return rows;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useColorThemeAdmin() {
  const q = useColorThemeFetcher();
  const [draft, setDraft] = useState<Record<string, ThemeStyle>>({});

  useEffect(() => {
    if (!q.data) return;
    const next: Record<string, ThemeStyle> = {};
    for (const r of q.data) {
      if (!/^field_slot_[1-4]_option_(?:[1-9]|1[0-5])$/.test(r.theme_key)) continue;
      next[r.theme_key] = {
        bg_color: r.bg_color,
        text_color: r.text_color,
        border_color: r.border_color,
      };
    }
    for (let fieldSlot = 0; fieldSlot < 4; fieldSlot++) {
      for (let option = 0; option < FIELD_OPTION_COUNT; option++) {
        const k = getFieldSlotOptionKey(fieldSlot, option);
        if (!next[k]) {
          const d = DEFAULT_THEME[k];
          next[k] = { bg_color: d.bg_color, text_color: d.text_color, border_color: d.border_color };
        }
      }
    }
    setDraft(next);
  }, [q.data]);

  return { query: q, draft, setDraft };
}
